import React, { useEffect, useState } from "react";
import { Button, Card, Spinner } from "@heroui/react";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { navigatePortal } from "../lib/navigation";

const DEADLINE = "2026-09-27";

export function ComplianceOverviewPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [guarantees, setGuarantees] = useState(null);
  const [aiLabels, setAiLabels] = useState(null);
  const [openCases, setOpenCases] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch("/api/guarantees/status").catch(() => null),
      apiFetch("/api/ai_labels/status").catch(() => null),
      apiFetch("/api/withdrawal_requests/stats").catch(() => null)
    ])
      .then(([g, a, stats]) => {
        if (cancelled) return;
        setGuarantees(g);
        setAiLabels(a);
        setOpenCases(stats?.open ?? stats?.open_count ?? stats?.by_status?.open ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <Spinner label={t("common.loading")} />;

  return (
    <div className="flex flex-col gap-6 p-4">
      <h1 className="text-2xl font-semibold">{t("overview.pageTitle")}</h1>
      <Card className="p-4">
        <p className="font-medium">{t("overview.deadlineHeading")}</p>
        <p>{t("overview.deadlineBody", { date: DEADLINE })}</p>
      </Card>
      <p>{t("overview.tagline")}</p>
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="text-lg font-medium">{t("overview.withdrawalsTitle")}</h2>
          {openCases != null && (
            <p>
              {t("overview.openCasesLabel")}: {openCases}
            </p>
          )}
          <Button onPress={() => navigatePortal("/portal/withdrawals")}>
            {t("overview.openWithdrawals")}
          </Button>
        </Card>
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="text-lg font-medium">{t("overview.guaranteesTitle")}</h2>
          <p>
            {t("overview.subscriptionLabel")}:{" "}
            {guarantees?.subscribed ? t("overview.statusReady") : t("overview.statusNeeded")}
          </p>
          <p>
            {t("overview.blockLabel")}:{" "}
            {guarantees?.block_present ? t("overview.statusReady") : t("overview.statusNeeded")}
          </p>
          <Button onPress={() => navigatePortal("/portal/guarantees")}>
            {t("overview.openGuarantees")}
          </Button>
        </Card>
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="text-lg font-medium">{t("overview.aiLabelsTitle") || "AI Labels"}</h2>
          <p>
            {t("overview.subscriptionLabel")}:{" "}
            {aiLabels?.subscribed ? t("overview.statusReady") : t("overview.statusNeeded")}
          </p>
          <p>
            {t("overview.blockLabel")}:{" "}
            {aiLabels?.block_present ? t("overview.statusReady") : t("overview.statusNeeded")}
          </p>
          <Button onPress={() => navigatePortal("/portal/ai-labels")}>
            {t("overview.openAiLabels") || "Open AI Labels"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
